// src/pages/PropertyDetail/PropertyDetail.js
import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  Chip,
  IconButton,
  Button,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  alpha,
  Avatar,
  Rating,
  Stack
} from '@mui/material'
import {
  Favorite,
  FavoriteBorder,
  LocationOn,
  Home,
  Bathtub,
  DirectionsCar,
  AspectRatio,
  Phone,
  Email,
  ArrowBack,
  Pool,
  Grass,
  Elevator,
  Chair,
  LockRounded,
  LoginRounded,
  RateReview,
  Business,
  BusinessCenter,
  Villa,
  Apartment,
  Terrain,
  Store,
  Agriculture,
  Handshake,
  Sell,
  Balcony,
  KingBed,
  Checkroom,
  AcUnit,
  OutdoorGrill,
  FitnessCenter,
  Security,
  Celebration,
  ChildCare,
  Laptop,
  Restaurant,
  NewReleases,
  SolarPower,
  Pets,
  Accessible,
  WhatsApp,
  Share
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { propertiesAPI } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import ReviewModal from '../../components/ReviewModal/ReviewModal'
import GoogleMap from '../../components/GoogleMap/GoogleMap'
import axios from 'axios'
import { CardImage } from './components/CardImage'
import { evento } from '../../analytics'

const PropertyDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [property, setProperty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedImage, setSelectedImage] = useState(0)
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [sellerData, setSellerData] = useState(null)

  useEffect(() => {
    loadPropertyData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const loadPropertyData = async () => {
    try {
      const response = await propertiesAPI.detail(id)
      setProperty(response.data)

      // Carregar dados do vendedor
      if (response.data.owner_username) {
        loadSellerData(response.data.owner_username)
      }
    } catch (error) {
      console.error('Erro ao carregar imóvel:', error)
      setError('Imóvel não encontrado')
    } finally {
      setLoading(false)
    }
  }

  const loadSellerData = async (username) => {
    try {
      const response = await axios.get(
        `https://umarkt.com.br/api/sellers/${username}/`
        // `http://127.0.0.1:8000/api/sellers/${username}/`
      )
      setSellerData(response.data)
    } catch (error) {
      console.error('Erro ao carregar dados do vendedor:', error)
    }
  }

  const handleFavoriteToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    try {
      await propertiesAPI.toggleFavorite(property.id)
      setProperty((prev) => ({
        ...prev,
        is_favorited: !prev.is_favorited
      }))
    } catch (error) {
      console.error('Erro ao favoritar:', error)
    }
  }

  const handleReviewCreated = () => {
    // Recarregar dados do vendedor para atualizar avaliações
    if (property.owner_username) {
      loadSellerData(property.owner_username)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price)
  }

  const getPropertyTypeLabel = (type) => {
    const types = {
      casa: 'Casa',
      apartamento: 'Apartamento',
      terreno: 'Terreno',
      comercial: 'Comercial',
      rural: 'Rural'
    }
    return types[type] || type
  }

  const getPropertyTypeIcon = (type) => {
    const icons = {
      casa: Villa,
      apartamento: Apartment,
      terreno: Terrain,
      comercial: Store,
      rural: Agriculture
    }
    return icons[type] || Business
  }

  const getTransactionColor = (type) => {
    return type === 'venda' ? 'success' : 'info'
  }

  if (loading) {
    return (
      <Container maxWidth='lg'>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error || !property) {
    return (
      <Container maxWidth='lg'>
        <Alert severity='error' sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/imoveis')}
          >
            Voltar à busca
          </Button>
        </Box>
      </Container>
    )
  }

  const images = property.images || []
  const hasImages = images.length > 0
  const sellerProfile = sellerData?.profile
  const sellerReviews = sellerData?.reviews

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/imoveis')}>
          Voltar à busca
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Coluna Esquerda - Imagens e Descrição */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Imagens */}
          <CardImage
            property={property}
            images={images}
            selectedImage={selectedImage}
            hasImages={hasImages}
          />

          {/* Miniaturas */}
          {hasImages && images.length > 1 && (
            <Box
              sx={{ display: 'flex', gap: 1, mb: 3, overflowX: 'auto', pb: 1 }}
            >
              {images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: 100,
                    height: 80,
                    cursor: 'pointer',
                    border: selectedImage === index ? 2 : 1,
                    borderColor:
                      selectedImage === index ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => setSelectedImage(index)}
                >
                  <img
                    src={image.image}
                    alt={`${property.title} - ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Descrição */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant='h5' gutterBottom fontWeight='bold'>
              Descrição
            </Typography>
            <Typography
              variant='body1'
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
            >
              {property.description}
            </Typography>
          </Paper>

          {/* Características Especiais */}
          {(property.has_pool ||
            property.has_garden ||
            property.has_elevator ||
            property.is_furnished ||
            property.has_balcony ||
            property.has_suite ||
            property.has_closet ||
            property.has_air_conditioning ||
            property.has_grill ||
            property.has_gym ||
            property.has_24h_security ||
            property.has_party_room ||
            property.has_playground ||
            property.has_coworking ||
            property.has_gourmet_area ||
            property.is_new ||
            property.has_solar_energy ||
            property.is_pet_friendly ||
            property.is_accessible) && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant='h6' gutterBottom fontWeight='bold'>
                Características Especiais
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {property.has_pool && (
                  <Chip icon={<Pool />} label='Piscina' color='primary' />
                )}
                {property.has_garden && (
                  <Chip icon={<Grass />} label='Jardim' color='primary' />
                )}
                {property.has_balcony && (
                  <Chip
                    icon={<Balcony />}
                    label='Sacada / Varanda'
                    color='primary'
                  />
                )}
                {property.has_suite && (
                  <Chip icon={<KingBed />} label='Suíte' color='primary' />
                )}
                {property.has_closet && (
                  <Chip icon={<Checkroom />} label='Closet' color='primary' />
                )}
                {property.has_air_conditioning && (
                  <Chip
                    icon={<AcUnit />}
                    label='Ar-condicionado'
                    color='primary'
                  />
                )}
                {property.is_furnished && (
                  <Chip icon={<Chair />} label='Mobiliado' color='primary' />
                )}
                {property.has_grill && (
                  <Chip
                    icon={<OutdoorGrill />}
                    label='Churrasqueira'
                    color='primary'
                  />
                )}
                {property.has_gym && (
                  <Chip
                    icon={<FitnessCenter />}
                    label='Academia'
                    color='primary'
                  />
                )}
                {property.has_24h_security && (
                  <Chip
                    icon={<Security />}
                    label='Portaria 24h'
                    color='primary'
                  />
                )}
                {property.has_party_room && (
                  <Chip
                    icon={<Celebration />}
                    label='Salão de festas'
                    color='primary'
                  />
                )}
                {property.has_playground && (
                  <Chip
                    icon={<ChildCare />}
                    label='Playground'
                    color='primary'
                  />
                )}
                {property.has_coworking && (
                  <Chip icon={<Laptop />} label='Coworking' color='primary' />
                )}
                {property.has_gourmet_area && (
                  <Chip
                    icon={<Restaurant />}
                    label='Espaço gourmet'
                    color='primary'
                  />
                )}
                {property.is_new && (
                  <Chip
                    icon={<NewReleases />}
                    label='Imóvel novo'
                    color='primary'
                    variant='filled'
                  />
                )}
                {property.has_elevator && (
                  <Chip icon={<Elevator />} label='Elevador' color='primary' />
                )}
                {property.has_solar_energy && (
                  <Chip
                    icon={<SolarPower />}
                    label='Energia solar'
                    color='primary'
                  />
                )}
                {property.is_pet_friendly && (
                  <Chip icon={<Pets />} label='Pet-friendly' color='primary' />
                )}
                {property.is_accessible && (
                  <Chip
                    icon={<Accessible />}
                    label='Acessível para PCD'
                    color='primary'
                  />
                )}
              </Box>
            </Paper>
          )}

          {/* Localização */}
          <Paper sx={{ p: 3 }}>
            <Typography variant='h5' gutterBottom fontWeight='bold'>
              Localização
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <LocationOn color='primary' sx={{ mr: 1, mt: 0.5 }} />
              <Box>
                {property.street && (
                  <Typography variant='body1'>
                    {property.street}
                    {property.number && `, ${property.number}`}
                  </Typography>
                )}
                {property.complement && (
                  <Typography variant='body2' color='text.secondary'>
                    {property.complement}
                  </Typography>
                )}
                {property.neighborhood && (
                  <Typography variant='body2' color='text.secondary'>
                    {property.neighborhood}
                  </Typography>
                )}
                <Typography variant='body1' sx={{ mt: 0.5 }}>
                  {property.city} - {property.state}
                </Typography>
                {property.zipcode && (
                  <Typography variant='body2' color='text.secondary'>
                    CEP: {property.zipcode}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <GoogleMap
                latitude={property.latitude}
                longitude={property.longitude}
                zipcode={property.zipcode}
                street={property.street}
                number={property.number}
                complement={property.complement}
                neighborhood={property.neighborhood}
                city={property.city}
                state={property.state}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Coluna Direita - Info e Contato */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}
        >
          {/* Card Principal do Imóvel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2
              }}
            >
              <Box>
                <Chip
                  label={
                    property.transaction_type === 'venda' ? 'Venda' : 'Aluguel'
                  }
                  color={getTransactionColor(property.transaction_type)}
                  sx={{ mb: 1 }}
                />
                <Chip
                  label={getPropertyTypeLabel(property.property_type)}
                  variant='outlined'
                  sx={{ ml: 1, mb: 1 }}
                />
              </Box>

              {isAuthenticated && (
                <IconButton onClick={handleFavoriteToggle} size='large'>
                  {property.is_favorited ? (
                    <Favorite color='error' />
                  ) : (
                    <FavoriteBorder />
                  )}
                </IconButton>
              )}
            </Box>

            <Typography
              variant='h4'
              component='h1'
              fontWeight='bold'
              color='primary'
              gutterBottom
            >
              {property.price_on_request ? 'Valor a Combinar' : formatPrice(property.price)}
            </Typography>

            <Typography variant='h5' component='h2' gutterBottom>
              {property.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LocationOn color='action' sx={{ mr: 1 }} />
              <Typography color='text.secondary'>
                {property.city} - {property.state}
              </Typography>
            </Box>

            {/* Características em Grid */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#eeeeee',
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    }
                  }}
                >
                  <Home sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                  <Typography variant='h6' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {property.bedrooms}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Quartos
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#eeeeee',
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    }
                  }}
                >
                  <Bathtub
                    sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }}
                  />
                  <Typography variant='h6' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {property.bathrooms}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Banheiros
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#eeeeee',
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    }
                  }}
                >
                  <DirectionsCar
                    sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }}
                  />
                  <Typography variant='h6' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {property.garage_spaces}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Vagas
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#eeeeee',
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    }
                  }}
                >
                  <AspectRatio
                    sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }}
                  />
                  <Typography variant='h6' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {property.area}m²
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Área
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#eeeeee',
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    }
                  }}
                >
                  {React.createElement(
                    getPropertyTypeIcon(property.property_type),
                    {
                      sx: { fontSize: 28, color: 'primary.main', mb: 0.5 }
                    }
                  )}
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                    sx={{
                      mb: 0.5,
                      fontSize: '1rem',
                      lineHeight: 1.2,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getPropertyTypeLabel(property.property_type)}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Tipo
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#eeeeee',
                      transform: 'translateY(-2px)',
                      boxShadow: 1
                    }
                  }}
                >
                  {property.transaction_type === 'venda' ? (
                    <Sell
                      sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }}
                    />
                  ) : (
                    <Handshake
                      sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }}
                    />
                  )}
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                    sx={{ mb: 0.5, fontSize: '1rem' }}
                  >
                    {property.transaction_type === 'venda'
                      ? 'Venda'
                      : 'Aluguel'}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Finalidade
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Informações do Vendedor */}
            <Box sx={{ position: 'relative' }}>
              <Typography variant='h6' gutterBottom fontWeight='bold'>
                Contato
              </Typography>

              {/* Conteúdo das informações de contato */}
              <Box>
                {/* Info do Vendedor com Avatar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#eeeeee' }
                  }}
                  onClick={() =>
                    navigate(`/vendedor/${property.owner_username}`)
                  }
                >
                  <Avatar
                    src={sellerProfile?.avatar}
                    sx={{ width: 56, height: 56, mr: 2 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='subtitle1' fontWeight='bold'>
                      {property.owner_name || property.owner_username}
                    </Typography>
                    {property.owner?.creci && (
                      <Chip
                        icon={<BusinessCenter />}
                        label={`CRECI: ${property.owner.creci}`}
                        size='small'
                        color='primary'
                        variant='outlined'
                        sx={{ mt: 0.5, mb: 0.5 }}
                      />
                    )}
                    {property.owner_rating > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Rating
                          value={property.owner_rating}
                          readOnly
                          size='small'
                          precision={0.1}
                        />
                        <Typography variant='caption' sx={{ ml: 0.5 }}>
                          ({property.owner_reviews_count})
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {property.owner_phone && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      p: 1.5,
                      bgcolor: '#f9f9f9',
                      borderRadius: 1
                    }}
                  >
                    <WhatsApp sx={{ mr: 1, color: '#25D366' }} />
                    <Phone color='primary' sx={{ mr: 1 }} />
                    <Typography fontWeight='500'>
                      {property.owner_phone}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant='contained'
                  fullWidth
                  size='large'
                  startIcon={<Phone />}
                  sx={{ mb: 1 }}
                  href={`tel:${property.owner_phone}`}
                  onClick={() =>
                    evento('contato_telefone', {
                      imovel_id: property.id,
                      cidade: property.city,
                      tipo: property.property_type,
                      transacao: property.transaction_type
                    })
                  }
                >
                  Ligar Agora
                </Button>

                <Button
                  variant='outlined'
                  fullWidth
                  size='large'
                  startIcon={<WhatsApp />}
                  sx={{
                    mb: 1,
                    borderColor: '#25D366',
                    color: '#25D366',
                    '&:hover': {
                      borderColor: '#128C7E',
                      bgcolor: 'rgba(37, 211, 102, 0.08)'
                    }
                  }}
                  onClick={() => {
                    evento('contato_whatsapp', {
                      imovel_id: property.id,
                      cidade: property.city,
                      tipo: property.property_type,
                      transacao: property.transaction_type
                    })
                    const phoneNumber = property.owner_phone.replace(/\D/g, '')
                    const message = `Olá! Tenho interesse no imóvel: ${property.title}`
                    const whatsappUrl = `https://wa.me/55${phoneNumber}?text=${encodeURIComponent(
                      message
                    )}`
                    window.open(whatsappUrl, '_blank')
                  }}
                >
                  WhatsApp
                </Button>

                <Button
                  variant='outlined'
                  fullWidth
                  size='large'
                  startIcon={<Share />}
                  sx={{ mb: 2 }}
                  onClick={() => {
                    const url = window.location.href
                    if (navigator.share) {
                      navigator.share({
                        title: property.title,
                        text: `Confira este imóvel: ${property.title}`,
                        url: url
                      })
                    } else {
                      navigator.clipboard.writeText(url)
                    }
                  }}
                >
                  Compartilhar
                </Button>

                {/* Botão de Avaliar */}
                {isAuthenticated &&
                  user?.username !== property.owner_username && (
                    <Button
                      variant='outlined'
                      fullWidth
                      startIcon={<RateReview />}
                      onClick={() => setReviewModalOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      Avaliar Vendedor
                    </Button>
                  )}

                {/* Ver Perfil Completo */}
                <Button
                  variant='text'
                  fullWidth
                  onClick={() =>
                    navigate(`/vendedor/${property.owner_username}`)
                  }
                  sx={{ mt: 1 }}
                >
                  Ver Perfil Completo
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Reviews do Vendedor (se houver) */}
          {sellerReviews && sellerReviews.count > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant='h6' gutterBottom fontWeight='bold'>
                Avaliações Recentes
              </Typography>
              <Stack spacing={2}>
                {sellerReviews.results.slice(0, 3).map((review) => (
                  <Box
                    key={review.id}
                    sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar
                        src={review.reviewer.avatar}
                        sx={{ width: 32, height: 32, mr: 1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' fontWeight='bold'>
                          {review.reviewer.username}
                        </Typography>
                      </Box>
                      <Rating value={review.rating} readOnly size='small' />
                    </Box>
                    <Typography variant='body2' color='text.secondary'>
                      {review.comment.length > 100
                        ? `${review.comment.substring(0, 100)}...`
                        : review.comment}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Button
                fullWidth
                variant='text'
                sx={{ mt: 2 }}
                onClick={() => navigate(`/vendedor/${property.owner_username}`)}
              >
                Ver todas as avaliações ({sellerReviews.count})
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Modal de Avaliação */}
      <ReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        seller={{
          id:
            typeof property.owner === 'object'
              ? property.owner.id
              : property.owner,
          username: property.owner_username || property.owner?.username
        }}
        property={{ id: property.id, title: property.title }}
        onReviewCreated={handleReviewCreated}
      />
    </Container>
  )
}

export default PropertyDetail
